import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ShieldAlert } from "lucide-react";

const TYPES = ["maladie", "ravageur", "traitement", "incident", "quarantaine"];

export default function Sante() {
  const { t, lang } = useI18n();
  const [records, setRecords] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [domain, setDomain] = useState("agriculture");
  const [open, setOpen] = useState(false);
  const [newR, setNewR] = useState({ type: "maladie", target_id: "", issue: "", product: "", dosage: "", operator: "", date: "" });

  async function load() {
    const [r, p, po] = await Promise.all([api.get("/health_records"), api.get("/parcelles"), api.get("/ponds")]);
    setRecords(r.data.sort((a, b) => (b.date || "").localeCompare(a.date || "")));
    setParcelles(p.data); setPonds(po.data);
  }
  useEffect(() => { load(); }, []);

  const targets = domain === "agriculture" ? parcelles : ponds;
  const filtered = records.filter((r) => r.domain === domain);

  async function addRecord() {
    if (!newR.target_id || !newR.issue) return toast.error("Cible & description requises");
    const target = targets.find((x) => x.id === newR.target_id);
    try {
      await api.post("/health_records", {
        ...newR, domain, target_name: target?.name,
        date: newR.date ? new Date(newR.date).toISOString() : new Date().toISOString(),
      });
      setOpen(false); setNewR({ type: "maladie", target_id: "", issue: "", product: "", dosage: "", operator: "", date: "" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delRecord(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/health_records/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-6" data-testid="sante-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("biosecurity")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("health")}</h1>
          <p className="text-muted-foreground mt-1">{records.length} {t("health").toLowerCase()}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-health-btn"><Plus className="w-4 h-4 mr-2" />{t("health")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("health")} ({t(domain)})</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("type")}</Label>
                <Select value={newR.type} onValueChange={(v) => setNewR({ ...newR, type: v })}>
                  <SelectTrigger data-testid="health-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((x) => <SelectItem key={x} value={x}>{t(`health_${x}`) || x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{domain === "agriculture" ? t("parcelle") : t("ponds")}</Label>
                <Select value={newR.target_id} onValueChange={(v) => setNewR({ ...newR, target_id: v })}>
                  <SelectTrigger data-testid="health-target"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{targets.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>{t("issue")}</Label><Input data-testid="health-issue" value={newR.issue} onChange={(e) => setNewR({ ...newR, issue: e.target.value })} /></div>
              <div><Label>{t("product_used")}</Label><Input value={newR.product} onChange={(e) => setNewR({ ...newR, product: e.target.value })} /></div>
              <div><Label>{t("dosage")}</Label><Input value={newR.dosage} onChange={(e) => setNewR({ ...newR, dosage: e.target.value })} /></div>
              <div><Label>{t("operator")}</Label><Input value={newR.operator} onChange={(e) => setNewR({ ...newR, operator: e.target.value })} /></div>
              <div><Label>{t("date")}</Label><Input type="date" value={newR.date} onChange={(e) => setNewR({ ...newR, date: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addRecord} data-testid="health-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={domain} onValueChange={setDomain}>
        <TabsList>
          <TabsTrigger value="agriculture" data-testid="tab-health-agri">{t("agriculture")}</TabsTrigger>
          <TabsTrigger value="pisciculture" data-testid="tab-health-pisci">{t("pisciculture")}</TabsTrigger>
        </TabsList>
        <TabsContent value={domain} className="mt-4">
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border bg-secondary/50">
                  <tr>
                    <th className="p-3">{t("date")}</th><th>{t("type")}</th><th>{t("target")}</th>
                    <th>{t("issue")}</th><th>{t("product_used")}</th><th>{t("operator")}</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`health-row-${r.id}`}>
                      <td className="p-3">{formatDate(r.date, lang)}</td>
                      <td><Badge variant={r.type === "incident" || r.type === "maladie" ? "destructive" : "secondary"}>{t(`health_${r.type}`) || r.type}</Badge></td>
                      <td className="font-medium">{r.target_name}</td>
                      <td>{r.issue}</td>
                      <td>{r.product}{r.dosage && r.dosage !== "—" ? ` (${r.dosage})` : ""}</td>
                      <td>{r.operator || "—"}</td>
                      <td><Button variant="ghost" size="sm" onClick={() => delRecord(r.id)}><Trash2 className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><ShieldAlert className="w-4 h-4" />{t("no_data")}</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
